/**
 * Analytics API
 *
 * API functions for fetching analytics data from Dataverse
 */

import { fetcher } from "@/utils/api/dataverse/fetcher";
import { CustomerAdoptionStatusResponse, DateRangeFilterType, QuoteConversionByRegionResponse, ConversionAnalysisResponse, CustomerRankingByRevenueResponse, ProductPerformanceResponse } from "../types";

/**
 * Get customer adoption status data from Dataverse API
 * @param dateRange - The date range filter to apply (default: 'lastmonth')
 * @param region - Optional region filter
 * @returns Customer adoption status data
 */
export const getCustomerAdoptionStatusApi = async (
  dateRange: DateRangeFilterType = 'lastmonth',
  region?: number
): Promise<CustomerAdoptionStatusResponse> => {
  let url = `/CustomerAdoptionStatus?dateRange=${dateRange}`;
  if (region !== undefined) {
    url += `&region=${region}`;
  }
  const response = await fetcher(url);
  return response;
};

/**
 * Get quote conversion by region data from Dataverse API
 * @param dateRange - The date range filter to apply (default: 'all')
 * @param region - Optional region filter
 * @returns Quote conversion by region data
 */
export const getQuoteConversionByRegionApi = async (
  dateRange: string = 'all',
  region?: number
): Promise<QuoteConversionByRegionResponse> => {
  let url = `/QuoteConversionByRegion?dateRange=${dateRange}`;
  if (region !== undefined) {
    url += `&region=${region}`;
  }
  const response = await fetcher(url);
  return response;
};

/**
 * Get conversion analysis data from Dataverse API
 * @param dateRange - The date range filter to apply (default: 'all')
 * @param region - Optional region filter
 * @returns Conversion analysis data
 */
export const getConversionAnalysisApi = async (
  dateRange: string = 'all',
  region?: number
): Promise<ConversionAnalysisResponse> => {
  let url = `/ConversionAnalysis?dateRange=${dateRange}`;
  if (region !== undefined) {
    url += `&region=${region}`;
  }
  const response = await fetcher(url);
  return response;
};

/**
 * Get customer ranking by revenue data from Dataverse API
 * @param dateRange - The date range filter to apply (default: 'all')
 * @param region - Optional region filter
 * @returns Customer ranking by revenue data
 */
export const getCustomerRankingByRevenueApi = async (
  dateRange: string = 'all',
  region?: number
): Promise<CustomerRankingByRevenueResponse> => {
  let url = `/CustomerRankingByRevenue?dateRange=${dateRange}`;
  if (region !== undefined) {
    url += `&region=${region}`;
  }
  const response = await fetcher(url);
  return response;
};

/**
 * Get product performance data from Dataverse API
 * @param region - Optional region filter
 * @returns Product performance data
 */
export const getProductPerformanceApi = async (region?: number): Promise<ProductPerformanceResponse> => {
  let url = `/ProductPerformance`;
  if (region !== undefined) {
    url += `?region=${region}`;
  }
  const response = await fetcher(url);
  return response;
};
